import { useState, useEffect } from 'react';
import {
    Alert,
    Grid,
    Stack,
    Box,
    Card,
    CardActionArea,
    CardContent,
    Radio,
    TextField,
    Typography,
} from '@mui/material';
import { PhoneIphone, Call } from '@mui/icons-material';
import { sendVerificationCode, verifyCode } from 'api/verification';
import MuiButton from 'components/Common/Button/MuiButton';
import { PageLoadingCircle } from 'components/Common/Loading';
import { SEND_METHOD, VERIFICATION_STATUS } from 'enums/MemberVerification';
import PhoneSelectionModal from './PhoneSelectionModal';

type VerificationScreenProps = {
    setVerificationStatus: (status: string) => void;
    phoneNumberList: string[];
};

type ErrorDialog = {
    show: boolean;
    message: string;
};

const VerificationScreen = ({
    setVerificationStatus,
    phoneNumberList,
}: VerificationScreenProps) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isVerificationCodeSent, setIsVerificationCodeSent] =
        useState<boolean>(false);
    const [inputCode, setInputCode] = useState<string>();
    const [sendMethod, setSendMethod] = useState<SEND_METHOD>();
    const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string>('');
    const [showModal, setShowModal] = useState<boolean>(false);
    const [errorDialog, setErrorDialog] = useState<ErrorDialog>({
        show: false,
        message: '',
    });
    const [seconds, setSeconds] = useState<number>(0);

    useEffect(() => {
        if (seconds > 0) {
            const timerId = setInterval(() => {
                setSeconds(prev => prev - 1);
            }, 1000);
            return () => clearInterval(timerId);
        }
    }, [seconds]);


    const cardData = [
        {
            title: 'Text Message',
            icon: <PhoneIphone fontSize="medium" />,
            description:
                'Receive a verification code via text message to your phone number.',
            sendMethod: SEND_METHOD.SMS,
        },
        {
            title: 'Voice Call',
            icon: <Call fontSize="medium" />,
            description:
                'Receive a verification code via voice call to your phone.',
            sendMethod: SEND_METHOD.CALL,
        },
    ];

    const selectSendMethod = (sendMethod) => {
        setSendMethod(sendMethod);
    };

    const clearAll = () => {
        setIsVerificationCodeSent(false);
        setInputCode('');
        setSendMethod(undefined);
        setShowModal(false);
        setSelectedPhoneNumber('');
        setErrorDialog({
            show: false,
            message: '',
        });
    };

    const sendVerificationCodeOnClick = async (phoneNumber) => {
        setIsLoading(true);
        sendVerificationCode(phoneNumber, sendMethod!)
            .then((response: any) => {
                if (response.status == VERIFICATION_STATUS.PENDING) {
                    setIsVerificationCodeSent(true);
                    setSeconds(getRetryTimeout(response.sendCodeAttempts.length));
                } else {
                    setShowModal(false);
                    setErrorDialog({
                        show: true,
                        message: response.message,
                    });
                }
            })
            .catch((error) => {
                setShowModal(false);
                setErrorDialog({
                    show: true,
                    message: error.message,
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const checkVerificationCode = async () => {
        setIsLoading(true);
        verifyCode(selectedPhoneNumber, inputCode!)
            .then((response: any) => {
                if (!response.success) {
                    setErrorDialog({
                        show: true,
                        message: response.message,
                    });
                }
                if (
                    response.success &&
                    response.message == VERIFICATION_STATUS.APPROVED
                ) {
                    setVerificationStatus(response.message);
                } 
                if (response.message == VERIFICATION_STATUS.EXPIRED) {
                    setErrorDialog({
                        show: true,
                        message: 'This verification code has expired.',
                    });
                } 
                else {
                    setErrorDialog({
                        show: true,
                        message: 'Unable to verify code. Please check that you entered the correct code.',
                    });
                }
            })
            .catch(() => {
                setErrorDialog({
                    show: true,
                    message: 'Unable to verify code.',
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };
    const getRetryTimeout = (attemptNumber) => {
        switch (attemptNumber) {
            case 1:
                return 30;
            case 2:
                return 40;
            case 3:
                return 60;
            case 4:
                return 90;
            case 5:
                return 120;
            default:
                return 600;
        }
       }

    if (isLoading) {
        return <PageLoadingCircle />;
    }
    if (isVerificationCodeSent) {
        return (
            <Grid
                container
                spacing={2}
                sx={{ padding: 2, justifyContent: 'center' }}
            >
                <Grid item xs={12} sm={6}>
                    <Stack spacing={2}>
                        <Typography component="div" variant="h5">
                            Enter the verification code
                        </Typography>
                        {errorDialog.show && (
                            <Alert
                                severity="error"
                                onClose={() => {
                                    setErrorDialog({
                                        show: false,
                                        message: '',
                                    });
                                }}
                                sx={{
                                    marginBottom: 2,
                                }}
                            >
                                {errorDialog.message}
                            </Alert>
                        )}
                        <TextField
                            type="text"
                            name="verificationCode"
                            placeholder="Enter verification code"
                            onChange={(e) => setInputCode(e.target.value)}
                            value={inputCode}
                        />
                        <MuiButton
                            variant="contained"
                            disabled={!inputCode || inputCode?.length === 0}
                            onClick={checkVerificationCode}
                        >
                            Verify Code
                        </MuiButton>


                    </Stack>
                    <Stack spacing={2} sx={{ paddingTop: 5 }}>
                    <Typography component="div" variant="h5">
                        Having problems with your code?
                    </Typography>
                    <Typography component="div" variant="subtitle1">
                        Click "Resend code" to try again, or "Back to Previous Screen" to select a different phone number or send method.
                    </Typography>
                    <MuiButton
                            variant="contained"
                            disabled={seconds > 0}
                            onClick={() => sendVerificationCodeOnClick(selectedPhoneNumber)}
                        >
                            Resend Code {seconds > 0 ? `in ${seconds} seconds` : ''}
                        </MuiButton>
                        <MuiButton variant="outlined" onClick={clearAll}>
                            Back to Previous Screen
                        </MuiButton>
                    </Stack>
                </Grid>
            </Grid>
        );
    }
    return (
        <Grid
            container
            spacing={2}
            sx={{ padding: 2, justifyContent: 'center' }}
        >
            <Grid
                item
                xs={12}
                sx={{ justifyContent: 'center', textAlign: 'center' }}
            >
                <Stack spacing={2}>
                    <h1>Verification</h1>
                    <p>
                        Choose which method to verify your identity to continue.
                    </p>
                </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
                {errorDialog.show && (
                    <Alert
                        severity="error"
                        onClose={() => {
                            setErrorDialog({
                                show: false,
                                message: '',
                            });
                        }}
                        sx={{
                            marginBottom: 2,
                        }}
                    >
                        {errorDialog.message}
                    </Alert>
                )}
                <Stack spacing={4}>
                    {cardData.map((card) => (
                        <Card sx={{ display: 'flex' }}>
                            <CardActionArea
                                onClick={() =>
                                    selectSendMethod(card.sendMethod)
                                }
                            >
                                <Box sx={{ flexDirection: 'column' }}>
                                    <CardContent>
                                        <Stack direction="row" spacing={2}>
                                            <Radio
                                                checked={
                                                    sendMethod ==
                                                    card.sendMethod
                                                }
                                                onClick={() =>
                                                    selectSendMethod(
                                                        card.sendMethod
                                                    )
                                                }
                                            />
                                            <Stack
                                                direction="row"
                                                spacing={2}
                                                sx={{ paddingTop: '3px' }}
                                            >
                                                <Box sx={{ paddingTop: '3px' }}>
                                                    <PhoneIphone fontSize="medium" />
                                                </Box>
                                                <Typography
                                                    component="div"
                                                    variant="h5"
                                                >
                                                    {card.title}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                        <Typography
                                            variant="subtitle1"
                                            component="div"
                                            sx={{
                                                color: 'text.secondary',
                                                marginLeft: 7,
                                            }}
                                        >
                                            {card.description}
                                        </Typography>
                                    </CardContent>
                                </Box>
                            </CardActionArea>
                        </Card>
                    ))}
                </Stack>

                <Stack spacing={2} sx={{ paddingTop: 2 }}>
                    <MuiButton
                        variant="contained"
                        disabled={!sendMethod}
                        onClick={() => setShowModal(true)}
                    >
                        Next
                    </MuiButton>
                </Stack>

                <PhoneSelectionModal
                    isOpen={showModal}
                    onClose={() => setShowModal(false)}
                    phoneNumberList={phoneNumberList}
                    onSubmit={(selectedNumber) => {
                        setSelectedPhoneNumber(selectedNumber);
                        sendVerificationCodeOnClick(selectedNumber);
                    }}
                />
            </Grid>
        </Grid>
    );
};
export default VerificationScreen;
